/**
 * Helpers around `transactions.bank_status` — the raw provider-side
 * lifecycle string (today: Mercury's `pending | sent | cancelled | failed
 * | reversed | blocked`).
 *
 * The DB trigger `tg_transactions_qb_status_sync` (migration 70) already
 * forces `qb_post_status = 'skipped'` whenever `bank_status` lands in the
 * "money did not move" set, so the UI doesn't have to gate the Post
 * button on it. But the operator still needs a visible signal in the
 * Live feed and on the Detail page that the row is a failed/cancelled
 * wire rather than a generic super_admin-skipped row — that's what
 * these helpers feed.
 */

/**
 * Bank-side statuses that mean the transaction did not actually settle.
 * Anything here should never reach QuickBooks. Mirrors `bankFailStatuses`
 * in `supabase/functions/qbo-post-transaction/index.ts` and the trigger
 * in migration 70 — keep all three in lockstep.
 */
const BANK_FAIL_STATUSES = new Set<string>([
  'failed',
  'cancelled',
  'reversed',
  'blocked',
]);

export function isBankFailStatus(status: string | null | undefined): boolean {
  return !!status && BANK_FAIL_STATUSES.has(status);
}

/**
 * Title-case the raw status for display. Returns null when the row is in
 * a normal lifecycle state (sent / pending / NULL / anything we don't
 * recognise as a failure) so callers can `if (label)` to gate badges.
 */
export function bankFailLabel(status: string | null | undefined): string | null {
  if (!isBankFailStatus(status)) return null;
  // status is non-null thanks to isBankFailStatus.
  const s = status!;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
